import {
  IconCircleCheck,
  IconCircleX,
  IconInfoCircle,
} from '@tabler/icons-react';
import { BenefitStatus } from '@zorro/clients';
import { Pill, PillColor } from '@zorro/design-system/v2/Pill';
import { Tooltip } from '@zorro/design-system/v2/Tooltip';
import {
  IconHalfCircle,
  IconQuarterCircle,
  IconThreeQuarterCircle,
} from '@zorro/shared/assets';
import { Icon } from '@zorro/web/design-system';
import { useRoles } from '@zorro/web/ui-utils';
import { ReactElement } from 'react';
import { $enum } from 'ts-enum-util';

const getStatusConfig = (
  status: BenefitStatus | undefined | null,
  isEmployerAdmin: boolean
) => {
  return $enum.mapValue(status).with<
    | {
        tooltipLabel: string;
        label: string;
        badgeColor: PillColor;
        icon: ReactElement;
        isHidden?: boolean;
      }
    | undefined
  >({
    [BenefitStatus.AWAITING_MEDICAL]: {
      tooltipLabel:
        'This plan is pending until the primary medical plan is submitted to the carrier',
      label: 'Awaiting medical',
      badgeColor: 'orange',
      isHidden: isEmployerAdmin,
      icon: (
        <Icon
          color="var(--color-icon-fill-tag-orange)"
          icon={IconInfoCircle}
          size={24}
        />
      ),
    },
    [BenefitStatus.READY_TO_APPLY]: {
      tooltipLabel:
        'The plan is ready to be applied to the carrier and pending application',
      label: 'Ready to apply',
      badgeColor: 'yellow',
      isHidden: isEmployerAdmin,
      icon: (
        <Icon
          color="var(--color-icon-stroke-tag-yellow)"
          icon={IconInfoCircle}
          size={24}
        />
      ),
    },
    [BenefitStatus.AWAITING_EMPLOYEE]: {
      tooltipLabel:
        "Waiting on the employee to enroll with the carrier and report that it's done. No agent action needed until they do.",
      label: 'Awaiting employee',
      badgeColor: 'indigo',
      isHidden: isEmployerAdmin,
      icon: <IconQuarterCircle color="var(--color-icon-fill-tag-indigo)" />,
    },
    [BenefitStatus.CARRIER_APPLICATION_SENT]: {
      tooltipLabel:
        "The application has been submitted to the carrier. Awaiting the carrier's final confirmation",
      label: 'Carrier application sent',
      badgeColor: 'indigo',
      isHidden: isEmployerAdmin,
      icon: <IconHalfCircle color="var(--color-icon-fill-tag-indigo)" />,
    },
    [BenefitStatus.AWAITING_PAYMENT]: {
      tooltipLabel:
        "Confirmed in the carrier's book of business. Waiting on the initial premium payment to be marked completed, then it moves to Confirmed.",
      label: 'Awaiting payment',
      badgeColor: 'indigo',
      isHidden: isEmployerAdmin,
      icon: (
        <IconThreeQuarterCircle color="var(--color-icon-fill-tag-indigo)" />
      ),
    },
    [BenefitStatus.ENROLLMENT_CONFIRMED]: {
      tooltipLabel:
        'The plan enrollment has been finalized and confirmed by the carrier',
      label: 'Confirmed',
      badgeColor: 'green',
      icon: (
        <Icon
          color="var(--color-icon-fill-tag-green)"
          icon={IconCircleCheck}
          size={24}
        />
      ),
    },
    [BenefitStatus.ACTIVE]: {
      tooltipLabel: 'The plan is currently active',
      label: 'Active',
      badgeColor: 'green',
      icon: (
        <Icon
          color="var(--color-icon-fill-tag-green)"
          icon={IconCircleCheck}
          size={24}
        />
      ),
    },
    [BenefitStatus.ENDED]: {
      tooltipLabel: 'The plan coverage period ended',
      label: 'Ended',
      badgeColor: 'steel',
      icon: (
        <Icon
          color="var(--color-icon-fill-tag-steel)"
          icon={IconCircleX}
          size={24}
        />
      ),
    },
    [BenefitStatus.IN_CART]: undefined,
    [$enum.handleUnexpected]: undefined,
    [$enum.handleUndefined]: undefined,
    [$enum.handleNull]: undefined,
  });
};

type Props = {
  status?: BenefitStatus | null;
};

export const EmployeePagePlansBenefitCardStatus = ({ status }: Props) => {
  const { isEmployerAdmin } = useRoles();
  const statusConfig = getStatusConfig(status, isEmployerAdmin);
  if (!statusConfig || statusConfig.isHidden) {
    return null;
  }

  const { tooltipLabel, label, badgeColor, icon } = statusConfig;

  return (
    <Tooltip label={tooltipLabel}>
      <Pill variant="tag" color={badgeColor} leftSection={icon}>
        {label}
      </Pill>
    </Tooltip>
  );
};
